import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Form,
	Row,
	Col,
	Card,
	Badge,
	notification,
	Tag,
	Spin,
	Button,
	Table,
	Space,
	Divider,
	Tooltip,
} from "antd";
import { FormOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import equipmentRequestService from "../../services/equipmentRequest";
import approvalService from "../../services/approver";
import CommentSystem from "../../components/comment";
import CreatedByComponent from "../../components/commonDetails/createdBy";
import ApproversComponent from "../../components/commonDetails/approvers";
import InventoryShowComponent from "../../components/commonDetails/inventoryShow";
import CurrencyShowComponent from "../../components/commonDetails/currencyShow";
import ApproveDisapproveComponent from "../../components/commonDetails/approveDisapprove";
import TaskStatus from "../../components/commonDetails/taskStatus";
import FeedbackModal from "../../components/feedbackModal";
import AddApproversComponent from "../../components/commonDetails/manageApprovers";
import { useSelector } from "react-redux";
import PDFInventoryShowComponent from "../../components/commonDetails/inventoryShow/pdf";
import crfSetting from "../../services/crfSetting";
import DeploymentModal from "../../components/deploymentModal";
import TaskModal from "../../components/taskModal";
import { replaceLineBreaks } from "../../utils/helper";
import ViewCustomFields from "../../components/viewCustomFields";

const EquipmentRequestDetails = () => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();
	const [status, setStatus] = useState(null);
	const { id } = useParams();
	const [assignedUserId, setAssignedUserId] = useState(null);
	const navigate = useNavigate();
	const [formId] = useState(4);
	const { currentUser } = useSelector((state) => state.user);
	const [crfSettingData, setCrfSettingData] = useState([]);
	const [editInvMode, setEditInvMode] = useState(false);
	const [isPrintable, setIsPrintable] = useState(false);
	const [hasHighExpenseRevenue, setHasHighExpenseRevenue] = useState(false);
	const [filteredApprovers, setFilteredApprovers] = useState([]);
	const [specificReturnGroupId, setSpecificReturnGroupId] = useState([]);
	const hasEditPermission = currentUser?.["roles.permission"]?.some(p => p.name === "Workflow-Crf-edit"); 

	const handlePrintClick = () => {
		setIsPrintable(true);
		// Give React time to render the #ppp div, then print
		setTimeout(() => {
			window.print();
			// Optional: Close print view after printing dialog closes
			setIsPrintable(false);
		}, 500);
	};

	const getCrfSettings = async () => {
		setLoading(true);
		try {
			const response = await crfSetting.getCrfSettings();
			const crfSettingsData = response.data[0];
			setCrfSettingData(crfSettingsData);
			setLoading(false);
		} catch (error) {
			console.log(error);
			notification.error({
				message: "Error",
				description: "error",
			});
		}
	};

	useEffect(() => {
		getCrfSettings();
	}, []);

	const getEquipmentsRequestById = async () => {
		setLoading(true);
		await equipmentRequestService
			.getEquipmentsRequestById(id)
			.then((response) => {
				setData(response);
				setLoading(false);
			})
			.catch((error) => {
				navigate("/crf");
			});
	};

	useEffect(() => {
		getEquipmentsRequestById();
	}, []);

	useEffect(() => {
		if (data) {
			const mergedRequests = [
				...(data.equipmentRequests || []),
				...(data.softwareRequests || []),
				...(data.serviceRequests || []),
			];

			// Check for Revenue requests with high expense (>500,000)
			const highExpenseRevenue = mergedRequests.some(
				(request) =>
					request.expense_nature?.name === "Revenue" &&
					JSON.parse(request.asset_details || "[]").some(
						(asset) => asset.expected_expense > 500000
					)
			);
			setHasHighExpenseRevenue(highExpenseRevenue);

			const approvers = data.approvers || [];
			const filtered = approvers.filter((approver) => {
				const isRevenueApprover = approver.condition === 8;
				if (isRevenueApprover) {
					const hasRevenueRequest = mergedRequests.some(
						(req) => req.expense_nature?.name === "Revenue"
					);

					return hasRevenueRequest ? highExpenseRevenue : true;
				}
				return true; // Keep all other approvers
			});
			setFilteredApprovers(filtered);
		}
	}, [data]);
	const calculateAndApplyPageBreaks = () => {
		if (!isPrintable) return;

		setTimeout(() => {
			const pageHeight = 1122; // A4 height in pixels
			const minSectionHeight = 50; // Minimum height to keep a section together

			// Get all sections that need to stay together
			const sections = document.querySelectorAll("[data-print-section]");

			let currentPageHeight = 0;

			sections.forEach((section, index) => {
				const sectionHeight = section.offsetHeight;
				const sectionWithNextHeight =
					sectionHeight + (sections[index + 1]?.offsetHeight || 0);

				// Check if this section will fit on current page
				const fitsOnCurrentPage =
					currentPageHeight + sectionHeight <= pageHeight;

				// Check if section should start on new page
				if (
					!fitsOnCurrentPage ||
					currentPageHeight + sectionHeight > pageHeight - 100
				) {
					// If this section is small and next one would fit, keep them together
					if (
						sectionHeight < 150 &&
						sections[index + 1] &&
						currentPageHeight + sectionWithNextHeight <= pageHeight
					) {
						// Keep with next section
						section.style.pageBreakBefore = "auto";
					} else {
						// Force new page
						section.style.pageBreakBefore = "always";
						currentPageHeight = sectionHeight;
					}
				} else {
					section.style.pageBreakBefore = "auto";
					currentPageHeight += sectionHeight;
				}

				// Prevent content from breaking inside sections
				section.style.pageBreakInside = "avoid";
				section.style.breakInside = "avoid";
			});
		}, 500); // Increased delay for better rendering
	};
	const handleSubmit = async (values, isParallel) => {
		setLoading(true);
		const formattedValues = {
			status,
			approved_disapproved: false,
			form_id: 4,
			key: parseInt(id),
			reason: values.message,
			...(isParallel && { assigned_user_id: assignedUserId }),

			...(specificReturnGroupId &&
				Number(specificReturnGroupId) > 0 && {
					approver_id: Number(specificReturnGroupId),
				}),
		};

		const serviceMethod = isParallel
			? approvalService.updateParallelStatus
			: approvalService.updateStatus;

		try {
			const response = await serviceMethod(formattedValues);
			if (response.success) {
				notification.success({
					message: "Status",
					description: response.message,
				});
				form.resetFields();
				getEquipmentsRequestById(id);
			}
			navigate("/dashboard");
		} catch (error) {
			notification.error({
				message: "Error",
				description: error.response?.data?.message || "An error occurred",
			});
		} finally {
			setLoading(false);
		}
	};

	// Comment Condition
	let canComment = 0;
	if (data) {
		if (data.status != "Approved" && data.status != "Return") {
			canComment = 1;
		} else {
			if (data.comment_status == 1) {
				canComment = 1;
			}
		}
	}

	const Isadmin = currentUser.roles.includes("admin");

	if (data) {
		// For Checking Revenue and Capital in the current form
		const hasExpenseNatureOne = data.equipmentRequests?.some(
			(request) => request.expense_nature?.id === 1
		);
		const hasExpenseNatureTwo = data.equipmentRequests?.some(
			(request) => request.expense_nature?.id === 2
		);

		let expCap = 0;
		let expRev = 0;
		let purCondition = 0;

		if (hasExpenseNatureOne) {
			expCap = 7;
		}
		if (hasExpenseNatureTwo) {
			expRev = 8;
		}
		// For Checking Revenue and Capital in the current form

		// For Checking Purchase in the current form

		const parsedEquipmentRequests = data.equipmentRequests?.map((request) => {
			const newRequest = { ...request };

			if (typeof newRequest.asset_details === "string") {
				try {
					newRequest.asset_details = JSON.parse(newRequest.asset_details);
				} catch (e) {
					console.error("Failed to parse asset_details:", e);
					newRequest.asset_details = [];
				}
			}

			return newRequest;
		});

		const hasPurchaseAction = parsedEquipmentRequests?.some((request) =>
			request.asset_details?.some((detail) => detail.action === "Purchase")
		);

		if (hasPurchaseAction) {
			purCondition = 9;
		}
	}

	const getStatusColor = (status) => {
		switch (status) {
			case "Approved":
				return "green";
			case "Pending":
				return "yellow";
			default:
				return "blue";
		}
	};

	const tableData = filteredApprovers?.flatMap((approver) =>
		approver.users
			.filter(
				(user) =>
					user.status_at && // only users with a datetime in status_at
					!(
						user.is_parallel === false &&
						(user.status === "Approved" ||
							user.status === "Disapproved" ||
							user.status === "Return") &&
						user.responded_by?.id !== user.id &&
						user.status_at
					)
			)
			.map((user) => ({
				approverName: approver.name,
				userName: user.name,
				status: user.status,
				status_at: user.status_at,
			}))
	);

	const columns = [
		{
			title: "Approver Name",
			dataIndex: "userName",
			key: "userName",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag style={{ fontSize: "11px" }} color={getStatusColor(status)}>
					{status}
				</Tag>
			),
		},
		{
			title: "Action Performed at",
			dataIndex: "status_at",
			key: "status_at",
		},
	];

	const adjustPageBreak = (elementId) => {
		const element = document.getElementById(elementId);
		if (!element) return;

		const viewportHeight = window.innerHeight;
		const elementBottom = element.getBoundingClientRect().bottom;
		const remainingSpace = viewportHeight - elementBottom;

		if (remainingSpace < 20) {
			element.style.pageBreakBefore = "always";
		} else {
			element.style.pageBreakBefore = "auto";
		}
	};

	const inventoryVisibility = () => {
		if (data) {
			const currentId = currentUser.user_id;
			const formInitUser = data?.created_by?.id;

			if (formInitUser != currentId) {
				setEditInvMode(true);
			} else {
				setEditInvMode(false);
			}
		}
	};

	useEffect(() => {
		adjustPageBreak("pbb");
		inventoryVisibility();
	}, [data]);

	const componentss = {
		header: {
			cell: (props) => <th {...props} style={{ fontSize: "12px" }} />,
		},
	};

	return (
		<DefaultLayout>
			<Spin spinning={loading}>
				{isPrintable && (
					<Card
						style={{
							minHeight: "100vh",
							border: "none",
						}}
						id='ppp'
					>
						<Row gutter={[24, 24]}>
							<Col span={4} style={{ textAlign: "left" }}>
								<img
									src={
										currentUser?.company?.logo
											? currentUser.company.logo.replace("/uploads", "")
											: "https://www.lucky-cement.com/wp-content/uploads/2017/02/lucky-logo.png"
									}
									alt='Logo'
									style={{ height: "36px", width: "auto", marginBottom: 8 }}
								/>
							</Col>
							<Col span={18} style={{ textAlign: "left" }}>
								<h2 style={{ marginBottom: 0 }}>
									<strong style={{ fontSize: "16px" }}>
										IT Equipment / Software / Services Requisition Form
									</strong>
								</h2>
							</Col>
							<Col span={2} style={{ textAlign: "right" }}>
								<h3> Internal </h3>
							</Col>
						</Row>
						<Row gutter={[24, 24]}>
							<Col span={24} style={{ textAlign: "center" }}>
								<b>Sequence No:</b>{" "}
								{String(data.sequence_no || "").toUpperCase()} &nbsp; | &nbsp;
								<b>Request Title:</b> {data.request_title} &nbsp; | &nbsp;
								<b>Status:</b> {data.status}
							</Col>
						</Row>
						<Row gutter={[24, 24]}>
							<Col span={24} style={{ textAlign: "center", paddingTop: "5px" }}>
								<span className='fs-10 '>
									<strong>Created At</strong>
								</span>
								<Tag style={{ fontSize: "10px" }}>&nbsp;{data.created_at} </Tag>

								<span className='fs-10 '>
									<strong>Created By</strong>&nbsp;
								</span>
								<Tag style={{ fontSize: "10px" }}>
									&nbsp;{data.created_by.name}{" "}
								</Tag>
								<span className='fs-10 '>
									<strong>Department:</strong>&nbsp;
								</span>
								<Tag style={{ fontSize: "10px" }}>
									&nbsp;{data.department.name}{" "}
								</Tag>
							</Col>
						</Row>

						<Divider />
						<div style={{ width: "100%" }}>
							{data && data.request_title && (
								<>
									<span style={{ fontSize: "16px" }}>Basic Information</span>

									<Row
										gutter={[10, 10]}
										className='avoid-break'
										style={{ border: "none", paddingBottom: "10px" }}
									>
										<Col span={6}>
											<span>
												<strong>Cost Center</strong>
											</span>
											<Tag style={{ fontSize: "10px", minWidth: "60%" }}>
												{data.cost_center_id?.cost_center}
											</Tag>
										</Col>

										<Col span={6}>
											<span>
												<strong>Location</strong>
											</span>
											<Tag style={{ fontSize: "10px", minWidth: "70%" }}>
												{data.location?.name}
											</Tag>
										</Col>

										{data.for_department && (
											<Col span={6}>
												<span>
													<strong>Department</strong>
												</span>
												<Tag style={{ fontSize: "10px", minWidth: "60%" }}>
													{data.for_department?.name}
												</Tag>
											</Col>
										)}

										{data.for_employee && (
											<Col span={6}>
												<span>
													<strong>Emp Name/Code</strong>
												</span>
												<Tag style={{ fontSize: "10px", minWidth: "74%" }}>
													{data.for_employee}
												</Tag>
											</Col>
										)}
									</Row>
								</>
							)}

							<div style={{ marginBottom: "0px" }}>
								{data?.equipmentRequests?.length > 0 && (
									<div data-print-section>
										<Card
											style={{ border: "none" }}
											styles={{ body: { padding: "5px 10px" } }}
										>
											{data.equipmentRequests.map((equipmentRequest, index) => (
												<div key={index} className='avoid-break'>
													<Row
														gutter={[10, 10]}
														key={index}
														align='middle'
														type='flex'
														justify='space-between'
													>
														<span
															style={{
																fontSize: "12px",
																fontWeight: "bold",
															}}
														>{`Equipment Request ${index + 1}`}</span>
														<Col>
															<span className='fs-10 mb-10'>Eq Name&nbsp;</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{equipmentRequest.equipment.name}
															</Tag>
														</Col>
														<Col>
															<span className='fs-10 mb-10'>Qty&nbsp;</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{equipmentRequest.qty}
															</Tag>
														</Col>
														<Col>
															<span className='fs-10 mb-10'>
																Equipment State&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{equipmentRequest?.state?.name}
															</Tag>
														</Col>
														<Col>
															<span className='fs-10 mb-10'>
																Exp Type&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{equipmentRequest.expense_type.name}
															</Tag>
														</Col>
														<Col>
															<span className='fs-10 mb-10'>
																Exp Nature&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{equipmentRequest.expense_nature.name}
															</Tag>
														</Col>
														<Col xs={24} sm={24} md={24} lg={24} xl={24}>
															<span>Business Justification &nbsp;</span>
															<Tag className="preserve-lines"
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																	minWidth: "80%",
																	minHeight: "35px",
																}}
															>
																{equipmentRequest.business_justification}
															</Tag>
														</Col>
													</Row>

													{editInvMode && (
														<div style={{ paddingTop: "5px" }}>
															<span
																style={{
																	fontSize: "12px",
																	fontWeight: "bold",
																}}
															>
																Filled By ITS Department:
															</span>

															<Row
																className='filledByRelatedDepartment'
																gutter={[12, 12]}
															>
																<Col xs={24} sm={24} md={24}>
																	<span>Currency: </span>
																	<Tag
																		style={{
																			fontSize: "10px",
																		}}
																	>
																		{equipmentRequest.currency}
																	</Tag>
																	<span>Price: </span>
																	<Tag
																		style={{
																			fontSize: "10px",
																		}}
																	>
																		{parseFloat(equipmentRequest.amount)
																			.toFixed(2)
																			.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
																	</Tag>
																</Col>
															</Row>
															<div
																style={{
																	borderTop: "1px solid #e8e8e8",
																	marginTop: "5px",
																	width: "100%",
																}}
															></div>
														</div>
													)}

													{editInvMode && (
														<Row
															className='mt-5 mb-5'
															align='middle'
															type='flex'
															justify='space-between'
															gutter={[24, 24]}
														>
															<Col>
																{equipmentRequest.asset_details &&
																	JSON.parse(
																		equipmentRequest.asset_details
																	).map((assetDetail, innerIndex) => (
																		<div
																			key={innerIndex}
																			className='pbb'
																			id='pbb'
																			style={{
																				marginTop: "10px",
																				marginBottom: "10px",
																			}}
																		>
																			<span
																				style={{
																					fontWeight: "bold",
																				}}
																			>
																				Equipment Inventory Asset Details{" "}
																			</span>
																			{innerIndex + 1}
																			<PDFInventoryShowComponent
																				key={innerIndex}
																				assetDetail={assetDetail}
																				currency={equipmentRequest.currency}
																				crfSettingData={crfSettingData}
																			/>
																		</div>
																	))}
															</Col>
														</Row>
													)}
												</div>
											))}
										</Card>
									</div>
								)}

								{data?.softwareRequests?.length > 0 && (
									<div data-print-section>
										<Card
											style={{ border: "none" }}
											styles={{ body: { padding: "5px 10px" } }}
										>
											<span style={{ fontSize: "16px" }}>
												Software Information
											</span>

											{data.softwareRequests.map((softwareRequest, index) => (
												<div key={index} className='avoid-break'>
													<Row
														gutter={[10, 10]}
														className='mt-5 mb-5'
														align='middle'
														type='flex'
														justify='space-between'
													>
														<span
															style={{
																fontSize: "12px",
																fontWeight: "bold",
															}}
														>{`Software Request ${index + 1}`}</span>

														<Col>
															<span className='fs-10 mb-10'>
																Software Name &nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{softwareRequest.name}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>
																Version &nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{softwareRequest.version}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>Qty &nbsp;</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{Number(softwareRequest.qty) || 0}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>
																Exp Type &nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{softwareRequest.expense_type.name}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>
																Exp Nature &nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{softwareRequest.expense_nature.name}
															</Tag>
														</Col>

														<Col xs={24} sm={24} md={24} lg={24} xl={24}>
															<span>Business Justification &nbsp;</span>
															<Tag className="preserve-lines"
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																	minWidth: "80%",
																	minHeight: "35px",
																}}
															>
																{softwareRequest.business_justification}
															</Tag>
														</Col>
													</Row>

													{editInvMode && (
														<div style={{ paddingTop: "5px" }}>
															<span
																style={{
																	fontSize: "12px",
																	fontWeight: "bold",
																}}
															>
																Filled By ITS Department:
															</span>

															<Row
																className='filledByRelatedDepartment'
																gutter={[12, 12]}
															>
																<Col xs={24} sm={24} md={24}>
																	<span>Currency: </span>
																	<Tag
																		style={{
																			fontSize: "10px",
																		}}
																	>
																		{softwareRequest.currency}
																	</Tag>
																	<span>Price: </span>
																	<Tag
																		style={{
																			fontSize: "10px",
																		}}
																	>
																		{parseFloat(softwareRequest.amount)
																			.toFixed(2)
																			.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
																	</Tag>
																</Col>
															</Row>
														</div>
													)}

													{editInvMode && (
														<Row
															className='mt-5 mb-5'
															align='middle'
															type='flex'
															justify='space-between'
															gutter={[24, 24]}
														>
															<Col>
																{softwareRequest.asset_details &&
																	JSON.parse(softwareRequest.asset_details).map(
																		(assetDetail, innerIndex) => (
																			<div
																				key={innerIndex}
																				className='pbb'
																				id='pbb'
																				style={{
																					marginTop: "10px",
																					marginBottom: "10px",
																				}}
																			>
																				<span
																					style={{
																						fontWeight: "bold",
																					}}
																				>
																					Software Inventory Asset Details{" "}
																					{innerIndex + 1}
																				</span>
																				<Divider
																					style={{
																						marginTop: "5px",
																						marginBottom: "5px",
																					}}
																				/>
																				<PDFInventoryShowComponent
																					key={innerIndex}
																					assetDetail={assetDetail}
																					currency={softwareRequest.currency}
																					crfSettingData={crfSettingData}
																				/>
																			</div>
																		)
																	)}
															</Col>
														</Row>
													)}
												</div>
											))}
										</Card>
									</div>
								)}

								{data?.serviceRequests?.length > 0 && (
									<div data-print-section>
										<Card
											style={{ border: "none" }}
											styles={{ body: { padding: "5px 10px" } }}
										>
											<span style={{ fontSize: "16px" }}>
												Service Information
											</span>

											{data.serviceRequests.map((serviceRequest, index) => (
												<div key={index} className='avoid-break'>
													<Row
														gutter={[10, 10]}
														className='mt-5 mb-5'
														align='middle'
														type='flex'
														justify='space-between'
													>
														<span
															style={{
																fontSize: "12px",
																fontWeight: "bold",
															}}
														>{`Service Request ${index + 1}`}</span>

														<Col>
															<span className='fs-10 mb-10'>
																Service Name&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{serviceRequest.name}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>
																Equipment State&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{serviceRequest.state.name}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>
																Exp Type&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{serviceRequest.expense_type.name}
															</Tag>
														</Col>

														<Col>
															<span className='fs-10 mb-10'>
																Exp Nature&nbsp;
															</span>
															<Tag
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																}}
															>
																&nbsp;{serviceRequest.expense_nature.name}
															</Tag>
														</Col>

														<Col xs={24} sm={24} md={24} lg={24} xl={24}>
															<span>Business Justification &nbsp;</span>
															<Tag className="preserve-lines"
																style={{
																	fontSize: "10px",
																	display: "block",
																	marginTop: "4px",
																	minWidth: "80%",
																	minHeight: "35px",
																}}
															>
																{serviceRequest.business_justification}
															</Tag>
														</Col>
													</Row>

													{editInvMode && (
														<div style={{ paddingTop: "5px" }}>
															<span
																style={{
																	fontSize: "12px",
																	fontWeight: "bold",
																}}
															>
																Filled By ITS Department:
															</span>

															<Row
																className='filledByRelatedDepartment'
																gutter={[12, 12]}
															>
																<Col xs={24} sm={24} md={24}>
																	<span>Currency: </span>
																	<Tag
																		style={{
																			fontSize: "10px",
																		}}
																	>
																		{serviceRequest.currency}
																	</Tag>
																	<span>Price: </span>
																	<Tag
																		style={{
																			fontSize: "10px",
																		}}
																	>
																		{parseFloat(serviceRequest.amount)
																			.toFixed(2)
																			.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
																	</Tag>
																</Col>
															</Row>
														</div>
													)}

													{editInvMode && (
														<Row
															className='mt-5 mb-5'
															align='middle'
															type='flex'
															justify='space-between'
															gutter={[24, 24]}
														>
															<Col>
																{serviceRequest.asset_details &&
																	JSON.parse(serviceRequest.asset_details).map(
																		(assetDetail, innerIndex) => (
																			<div
																				key={innerIndex}
																				className='pbb'
																				id='pbb'
																				style={{
																					marginTop: "10px",
																					marginBottom: "10px",
																				}}
																			>
																				<span
																					style={{
																						fontWeight: "bold",
																					}}
																				>
																					Service Inventory Asset Details{" "}
																					{innerIndex + 1}
																				</span>
																				<Divider
																					style={{
																						marginTop: "5px",
																						marginBottom: "5px",
																					}}
																				/>
																				<PDFInventoryShowComponent
																					key={innerIndex}
																					assetDetail={assetDetail}
																					currency={serviceRequest.currency}
																					crfSettingData={crfSettingData}
																				/>
																			</div>
																		)
																	)}
															</Col>
														</Row>
													)}
												</div>
											))}
										</Card>
									</div>
								)}
							</div>
						</div>

						<div className='mb-5 avoid-break'>
							<Row gutter={[8, 8]}>
								<Col lg={24} md={24} sm={24} xs={24}>
									<table className='basic-print-table'>
										<thead>
											<tr>
												<th>Approver Name</th>
												<th>Status</th>
												<th>Action Performed at</th>
											</tr>
										</thead>
										<tbody>
											{tableData && tableData.length > 0 ? (
												tableData.map((row, index) => (
													<tr key={index}>
														<td>{row.userName}</td>
														<td>
															<span
																style={{
																	color:
																		getStatusColor(row.status) === "green"
																			? "green"
																			: getStatusColor(row.status) === "yellow"
																			? "#DAA520"
																			: "black",
																}}
															>
																{row.status}
															</span>
														</td>
														<td>{row.status_at}</td>
													</tr>
												))
											) : (
												<tr>
													<td colSpan='3' style={{ textAlign: "center" }}>
														No approver data available
													</td>
												</tr>
											)}
										</tbody>
									</table>
									{/* <p style={{ marginTop: "15px", fontSize: "12px" }}>
										This document has been approved through the system-based
										workflow. Detailed information for CRF ID: {id} can be
										accessed from this link.{" "}
										<a
											href={window.location.href}
											target='_blank'
											rel='noopener noreferrer'
										>
											View Details
										</a>
									</p> */}
								</Col>
							</Row>
						</div>
					</Card>
				)}

				{/* PRINT END PAGE ================================================================================================   */}

				{/*Non Printable*/}

				{/* WEB PAGE ================================================================================================   */}

				<Card style={{ minHeight: "100vh" }} id='qqq'>
					<Row gutter={[24, 24]}>
						<Col lg={5} md={5} sm={24} xs={24}>
							<CreatedByComponent data={data} />
							<Button
								type='primary'
								onClick={handlePrintClick}
								style={{
									marginTop: "20px",
									marginBottom: "20px",
									width: "100%",
								}}
							>
								Print Document
							</Button>
							{data &&
								(data.status === "Pending" || data.status === "Approved") &&
								hasEditPermission && (
									<AddApproversComponent data={data && data} formId={formId} />
								)}
							<ApproversComponent approvers={filteredApprovers} />
						</Col>

						<Col lg={19} md={19} sm={24} xs={24}>
							<div>
								{data?.deployments !== null && (
									<DeploymentModal
										title='Deployments'
										data_deployment={data?.deployments}
										style={{ marginright: "0" }}
									/>
								)}
								{data?.assigned_task?.assign_task_teams[0]?.members?.some(
									(m) => m.start_at && m.due_at
								) && (
									<TaskModal
										title='Tasks Logs'
										data_tasks={data.assigned_task.assign_task_teams[0].members}
										style={{ marginright: "0" }}
									/>
								)}
								{data?.approved_disapproved === true && (
									<div
										style={{
											position: "sticky",
											top: 1,
											zIndex: 999,
											background: "#fff",
											padding: "5px",
											borderRadius: "4px",
										}}
									>
										<Spin spinning={loading}>
											{data?.task_status?.id !== null && (
												<FeedbackModal
													title='QA Feedback'
													formId={formId}
													assurable_id={id}
													style={{ marginright: "0" }}
												/>
											)}
											<b>Approval Request</b>
											<ApproveDisapproveComponent
												form={form}
												handleStatus={(values) => handleSubmit(values, false)}
												loading={loading}
												setStatus={setStatus}
												dataRecord={data}
												setSpecificReturnGroupId={setSpecificReturnGroupId}
											/>
										</Spin>
									</div>
								)}

								{data?.parallel_approved_disapproved === true && (
									<div
										style={{
											position: "sticky",
											top: data?.approved_disapproved ? "150px" : "1px", // Adjust the top value as needed
											zIndex: 999,
											background: "#fff", // Add a background color
											padding: "5px", // Add padding for better visual separation
											borderRadius: "4px", // Optional: Add rounded corners
										}}
									>
										<Spin spinning={loading}>
											<b>Parallel Approval Request</b>
											<ApproveDisapproveComponent
												form={form}
												handleStatus={(values) => handleSubmit(values, true)}
												loading={loading}
												setStatus={setStatus}
												parallel={true}
												parallelUsers={
													data && data.parallel_approved_disapproved_users
												}
												setAssignedUserId={setAssignedUserId}
												reloadData={getEquipmentsRequestById}
												dataRecord={data}
												setSpecificReturnGroupId={setSpecificReturnGroupId}
											/>
										</Spin>
									</div>
								)}

								<div className='mb-10'>
									{data?.assigned_task_to_me === true && (
										<div
											style={{
												position: "sticky",
												top: "1px",
												zIndex: 999,
												background: "#fff", // Add a background color
												padding: "5px", // Add padding for better visual separation
												borderRadius: "4px", // Optional: Add rounded corners
											}}
										>
											<TaskStatus
												data={data}
												reloadData={getEquipmentsRequestById}
												formId={formId}
											/>
										</div>
									)}
								</div>

								{data && data.request_title && (
									<Badge.Ribbon>
										<Card title='Basic Information' className='mb-10'>
											<Row gutter={[16, 16]}>
												<Col xs={24} sm={12} md={3} lg={3} xl={3}>
													<p className='fs-12 mb-10'>Sequence No</p>
													<Tag>{data.sequence_no}</Tag>
												</Col>

												<Col xs={24} sm={12} md={5} lg={5} xl={5}>
													<p className='fs-12 mb-10'>Request Title</p>
													<Tooltip title={data.request_title} placement='top'>
														<Tag
															style={{
																fontSize: "clamp(8px, 1.2vw, 12px)",
																maxWidth: "100%",
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
															}}
														>
															{data.request_title}
														</Tag>
													</Tooltip>
												</Col>

												<Col xs={24} sm={12} md={4} lg={4} xl={4}>
													<p className='fs-12 mb-10'>Cost Center</p>
													<Tag>{data.cost_center_id?.cost_center}</Tag>
												</Col>

												<Col xs={24} sm={12} md={4} lg={4} xl={4}>
													<p className='fs-12 mb-10'>Location</p>
													<Tag>{data.location?.name}</Tag>
												</Col>
												{data.for_employee && (
													<Col xs={24} sm={12} md={4} lg={4} xl={4}>
														<p className='fs-12 mb-10'>For Employee</p>
														<Tooltip title={data.for_employee} placement='top'>
															<Tag
																style={{
																	fontSize: "clamp(8px, 1.2vw, 12px)",
																	maxWidth: "100%",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{data.for_employee}
															</Tag>
														</Tooltip>
													</Col>
												)}
												{data.for_department && (
													<Col xs={24} sm={12} md={4} lg={4} xl={4}>
														<p className='fs-12 mb-10'>For Department</p>
														<Tag>{data.for_department?.name}</Tag>
													</Col>
												)}
												{data && data.assigned_task?.start_at && (
													<Col xs={24} sm={12} md={8} lg={4} xl={4}>
														<p className='fs-12 mb-10 '>Start Date</p>
														<Tag>{data.assigned_task?.start_at}</Tag>
													</Col>
												)}
												{data && data.assigned_task?.due_at && (
													<Col xs={24} sm={12} md={8} lg={4} xl={4}>
														<p className='fs-12 mb-10 '>Deadline</p>
														<Tag>{data.assigned_task?.due_at}</Tag>
													</Col>
												)}
												{data.task_status?.name && (
													<Col xs={24} sm={12} md={8} lg={6} xl={6}>
														<p className='fs-12 mb-10 bold'>Task Status</p>
														<Tag>{data.task_status.name}</Tag>
													</Col>
												)}
											</Row>
										</Card>
									</Badge.Ribbon>
								)}
								{data?.equipmentRequests?.length > 0 && (
									<Card
										title='Equipment Information'
										className='mb-10'
										style={{ border: "none" }}
									>
										{data.equipmentRequests.map((equipmentRequest, index) => (
											<Badge.Ribbon
												key={index}
												text={`Equipment Request ${index + 1}`}
											>
												<Card>
													<Row key={index} gutter={[16, 16]} className='mt-20'>
														<Col xs={24} sm={12} md={8} lg={6} xl={6}>
															<p className='fs-12 mb-10'>Equipment Name</p>
															<Tag label='Equipment Name'>
																{equipmentRequest.equipment.name}
															</Tag>
														</Col>

														<Col xs={24} sm={12} md={8} lg={6} xl={6}>
															<p className='fs-12 mb-10'>Quantity</p>
															<Tag label='Quantity'>{equipmentRequest.qty}</Tag>
														</Col>

														<Col xs={24} sm={12} md={8} lg={6} xl={6}>
															<p className='fs-12 mb-10'>Equipment State</p>
															<Tag label='Equipment State'>
																{equipmentRequest.state.name}
															</Tag>
														</Col>

														<Col xs={24} sm={12} md={8} lg={6} xl={6}>
															<p className='fs-12 mb-10'>Expense Type</p>
															<Tag label='Expense Type'>
																{equipmentRequest.expense_type.name}
															</Tag>
														</Col>

														<Col xs={24} sm={12} md={8} lg={6} xl={6}>
															<p className='fs-12 mb-10'>Expense Nature</p>
															<Tag label='Expense Nature'>
																{equipmentRequest.expense_nature.name}
															</Tag>
														</Col>

														<Col xs={24} sm={24} md={24} lg={24} xl={24}>
															<p className='fs-12 mb-10'>
																Business Justification
															</p>
															<Tag className="preserve-lines" label='Business Justification'>
																{equipmentRequest.business_justification}
															</Tag>
														</Col>

														{editInvMode && (
															<Col xs={24} sm={24} md={24} lg={24} xl={24}>
																<CurrencyShowComponent
																	data={equipmentRequest}
																	crfSettingData={crfSettingData}
																/>
															</Col>
														)}
														{editInvMode && (
															<Col span={24}>
																{equipmentRequest.asset_details &&
																	JSON.parse(
																		equipmentRequest.asset_details
																	).map((assetDetail, innerIndex) => (
																		<div key={innerIndex} className='mt-14'>
																			<Tag color='blue' className='fs-10'>
																				Equipment Inventory Asset Details{" "}
																				{innerIndex + 1}
																			</Tag>
																			<div key={innerIndex} className='mb-10'>
																				<span>
																					{" "}
																					<strong>
																						Equipment Inventory Asset Details
																					</strong>
																				</span>

																				<Divider
																					style={{
																						marginTop: "5px",
																						marginBottom: "5px",
																					}}
																				/>
																				<InventoryShowComponent
																					key={innerIndex}
																					assetDetail={assetDetail}
																					currency={equipmentRequest.currency}
																					crfSettingData={crfSettingData}
																				/>
																			</div>
																		</div>
																	))}
															</Col>
														)}
													</Row>
												</Card>
											</Badge.Ribbon>
										))}
									</Card>
								)}

								{data &&
									data.softwareRequests &&
									data.softwareRequests.length > 0 && (
										<Card title='Software Information' className='mt-20 mb-10'>
											{data.softwareRequests.map((softwareRequest, index) => (
												<Badge.Ribbon
													key={index}
													text={`Software Request ${index + 1}`}
												>
													<Card>
														<Row
															key={index}
															gutter={[16, 16]}
															className='mt-20'
														>
															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Software Name</p>
																<Tag>{softwareRequest.name}</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Software Version</p>
																<Tag>{softwareRequest.version}</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Quantity</p>
																<Tag>
																	{typeof softwareRequest.qty === "number"
																		? softwareRequest.qty
																		: Number(softwareRequest.qty) || 0}
																</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Expense Type</p>
																<Tag>{softwareRequest.expense_type.name}</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Expense Nature</p>
																<Tag>{softwareRequest.expense_nature.name}</Tag>
															</Col>

															<Col xs={24} sm={24} md={24} lg={24} xl={24}>
																<p className='fs-12 mb-10'>
																	Business Justification
																</p>
																<Tag className="preserve-lines">
																	{softwareRequest.business_justification}
																</Tag>
															</Col>

															{editInvMode && (
																<Col xs={24} sm={24} md={24} lg={24} xl={24}>
																	<CurrencyShowComponent
																		data={softwareRequest}
																		crfSettingData={crfSettingData}
																	/>
																</Col>
															)}
															{editInvMode && (
																<Col span={24}>
																	{softwareRequest.asset_details &&
																		JSON.parse(
																			softwareRequest.asset_details
																		).map((assetDetail, innerIndex) => (
																			<div key={innerIndex} className='mt-14'>
																				<span>
																					<strong>
																						Software Inventory Asset Details
																					</strong>
																				</span>
																				<InventoryShowComponent
																					key={innerIndex}
																					assetDetail={assetDetail}
																					currency={softwareRequest.currency}
																					crfSettingData={crfSettingData}
																				/>
																			</div>
																		))}
																</Col>
															)}
														</Row>
													</Card>
												</Badge.Ribbon>
											))}
										</Card>
									)}

								{data &&
									data.serviceRequests &&
									data.serviceRequests.length > 0 && (
										<Card
											title='Service Information'
											className='mt-20 mb-10 avoid-break'
										>
											{data.serviceRequests.map((serviceRequest, index) => (
												<Badge.Ribbon
													key={index}
													text={`Service Information ${index + 1}`}
												>
													<Card>
														<Row
															key={index}
															gutter={[16, 16]}
															className='mt-20 avoid-break'
														>
															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Service Name</p>
																<Tag>{serviceRequest.name}</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Equipment State</p>
																<Tag>{serviceRequest.state.name}</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Expense Type</p>
																<Tag>{serviceRequest.expense_type.name}</Tag>
															</Col>

															<Col xs={24} sm={12} md={8} lg={6} xl={6}>
																<p className='fs-12 mb-10'>Expense Nature</p>
																<Tag>{serviceRequest.expense_nature.name}</Tag>
															</Col>

															<Col xs={24} sm={24} md={24} lg={24} xl={24}>
																<p className='fs-12 mb-10'>
																	Business Justification
																</p>
																<Tag className="preserve-lines">
																	{serviceRequest.business_justification}
																</Tag>
															</Col>

															{editInvMode && (
																<Col xs={24} sm={24} md={24} lg={24} xl={24}>
																	<CurrencyShowComponent
																		data={serviceRequest}
																		crfSettingData={crfSettingData}
																	/>
																</Col>
															)}

															{editInvMode && (
																<Col span={24}>
																	{serviceRequest.asset_details &&
																		JSON.parse(
																			serviceRequest.asset_details
																		).map((assetDetail, innerIndex) => (
																			<div key={innerIndex} className='mb-10'>
																				<span>
																					<strong>
																						Service Inventory Asset Details
																					</strong>
																				</span>
																				<InventoryShowComponent
																					key={innerIndex}
																					assetDetail={assetDetail}
																					currency={serviceRequest.currency}
																					crfSettingData={crfSettingData}
																				/>
																			</div>
																		))}
																</Col>
															)}
														</Row>
													</Card>
												</Badge.Ribbon>
											))}
										</Card>
									)}

								{/* FETCH DATA CUSTOM FIELDS */}
								<ViewCustomFields customFields={data?.custom_fields} />
							</div>

							<CommentSystem
								id={id}
								form_id={4}
								status={data && data.status}
								comment_status={data && canComment}
							/>
						</Col>
					</Row>
				</Card>
			</Spin>
		</DefaultLayout>
	);
};

export default EquipmentRequestDetails;

const styles = `
@media print {
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  body, html {
    margin: 0 !important;
    padding: 0 !important;
    background: white;
  }

  #qqq,
  .ant-layout-header,
  .ant-layout-sider,
  .ant-btn,
  button,
  #mode-icon {
    display: none !important;
  }

  #ppp {
    display: block !important;
    width: 100% !important;
    overflow: visible !important;
  }

  #ppp .ant-row {
    display: flex !important;
    flex-wrap: wrap !important;
  }

  #ppp .ant-col {
    display: block !important;
  }

  /* Force desktop grid */
  #ppp .ant-col-md-3 { flex: 0 0 12.5%; max-width: 12.5%; }
  #ppp .ant-col-md-4 { flex: 0 0 16.66%; max-width: 16.66%; }
  #ppp .ant-col-md-6 { flex: 0 0 25%; max-width: 25%; }
  #ppp .ant-col-md-12 { flex: 0 0 50%; max-width: 50%; }
  #ppp .ant-col-md-24 { flex: 0 0 100%; max-width: 100%; }

  .avoid-break {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  #ppp .ant-card {
    border: 1px solid #d9d9d9 !important;
    box-shadow: none !important;
  }

  #ppp .ant-tag {
    color: #000 !important;
    border: 1px solid #ccc !important;
  }

  /* ✅ SINGLE table definition */
  .basic-print-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-family: sans-serif;
  }

  .basic-print-table th,
  .basic-print-table td {
    font-size: 8px !important;
    line-height: 1.3;
    padding: 4px 6px;
    border: 1px solid #999 !important;
    color: #000 !important;
  }

  .basic-print-table th {
    background-color: #f0f0f0 !important;
    font-weight: bold;
  }

  .basic-print-table thead {
    display: table-header-group;
  }

  .basic-print-table tr {
    page-break-inside: avoid;
  }
}`;
document.head.insertAdjacentHTML("beforeend", `<style>${styles}</style>`);
